import { ref } from 'vue'
import { get, set } from 'idb-keyval'

export interface Ward {
    code: number
    name: string
}

export interface District {
    code: number
    name: string
    wards?: Ward[]
}

export interface Province {
    code: number
    name: string
    division_type: string
    districts?: District[]
}

export function useVietnamAdminData() {
    const provinces = ref<Province[]>([])
    const wards = ref<Ward[]>([])
    const districts = ref<District[]>([])
    const loading = ref(false)
    const error = ref<string | null>(null)

    const loadVietnamData = async () => {
        loading.value = true
        error.value = null

        try {
            const cached = await get<Province[]>('vietnam_admin_data')
            if (cached && cached.length > 0) {
                provinces.value = cached.map(({ code, name, division_type }) => ({
                    code,
                    name,
                    division_type
                }))
                loading.value = false
                return
            }

            const res = await fetch('https://provinces.open-api.vn/api/v1/p/')
            if (!res.ok) {
                provinces.value = []
                loading.value = false
                return
            }

            const data: Province[] = await res.json()
            provinces.value = data
            await set('vietnam_admin_data', data)
        } catch (err: any) {
            console.error('Lỗi tải dữ liệu:', err)
            error.value = err.message
        } finally {
            loading.value = false
        }
    }

    const getListDistricts = async (provinceCode: number) => {
        loading.value = true
        error.value = null

        try {
            const cached = await get<Province[]>('vietnam_admin_data')
            if(!cached || (cached && cached.length === 0)) {
                return
            }
            const provinceSelected = cached.find((p) => p.code === provinceCode)
            const provinceSelectedIndex = cached?.findIndex((p) => p.code === provinceCode)
            if(provinceSelected && provinceSelected.districts && provinceSelected.districts.length > 0) {
                districts.value = provinceSelected.districts.map(({ code, name }) => ({
                    code,
                    name
                }))
                loading.value = false
                return
            }

            const res = await fetch('https://provinces.open-api.vn/api/v1/p/' + provinceCode + '?depth=2')
            if (!res.ok) {
                districts.value = []
                loading.value = false
                return
            }

            const provinceDetail = await res.json();
            districts.value = provinceDetail.districts ?? []
            cached[provinceSelectedIndex].districts = districts.value
            await set('vietnam_admin_data', JSON.parse(JSON.stringify(cached)))
        } catch (err: any) {
            console.error('Lỗi tải dữ liệu:', err)
            error.value = err.message
        } finally {
            loading.value = false
        }
    }

    const getListWards = async (provinceCode: number, districtCode: number) => {
        loading.value = true
        error.value = null

        try {
            const cached = await get<Province[]>('vietnam_admin_data')
            if(!cached || (cached && cached.length === 0)) {
                return
            }
            const provinceSelected = cached.find((p) => p.code === provinceCode)
            const provinceSelectedIndex = cached?.findIndex((p) => p.code === provinceCode)
            let districtSelectedIndex = -1
            if(provinceSelected && provinceSelected.districts && provinceSelected.districts.length > 0) {
                districts.value = provinceSelected.districts.map(({ code, name }) => ({
                    code,
                    name
                }))
                const districtSelected = districts.value.find((d) => d.code === districtCode)
                districtSelectedIndex = districts.value?.findIndex((d) => d.code === districtCode)
                if(districtSelected && districtSelected.wards && districtSelected.wards.length > 0) {
                    wards.value = districtSelected.wards.map(({ code, name }) => ({
                        code,
                        name
                    }))
                    loading.value = false
                    return
                }
            }

            const res = await fetch('https://provinces.open-api.vn/api/v1/d/' + districtCode + '?depth=2')
            if (!res.ok) {
                wards.value = []
                loading.value = false
                return
            }

            const districtDetail = await res.json();
            wards.value = districtDetail.wards ?? []
            const province = cached[provinceSelectedIndex]
            if (province?.districts?.[districtSelectedIndex]) {
                province.districts[districtSelectedIndex].wards = wards.value
            }
            await set('vietnam_admin_data', JSON.parse(JSON.stringify(cached)))
        } catch (err: any) {
            console.error('Lỗi tải dữ liệu:', err)
            error.value = err.message
        } finally {
            loading.value = false
        }
    }

    return { provinces, districts, wards, loadVietnamData, loading, error, getListDistricts, getListWards }
}
